// app/components/users/UserTable.jsx
import React from 'react';
import { Edit2, Trash2, Key } from 'lucide-react';
import { formatDateTime } from '../../utils/dateUtils';

const UserTable = ({ users, onEdit, onDelete, onUpdatePassword }) => {
    const getRoleBadgeClass = (roleCode) => {
        const roleClasses = {
            admin: 'bg-purple-100 text-purple-800',
            manager: 'bg-blue-100 text-blue-800',
            agent: 'bg-green-100 text-green-800',
            driver: 'bg-gray-100 text-gray-800',
            vendor_owner: 'bg-orange-100 text-orange-800',
            vendor_manager: 'bg-yellow-100 text-yellow-800',
        };
        return roleClasses[roleCode.toLowerCase()] || 'bg-gray-100 text-gray-800';
    };

    const renderRoles = (user) => {
        // Handle both old single role format and new multiple roles format
        if (user.roles && Array.isArray(user.roles) && user.roles.length > 0) {
            // New format: multiple roles
            return (
                <div className="flex flex-wrap gap-1">
                    {user.roles.map((role, index) => (
                        <span 
                            key={index}
                            className={`px-2 py-1 text-xs rounded-md capitalize ${getRoleBadgeClass(role.code || role.role_code)}`}
                            title={role.description || role.name}
                        >
                            {role.name || role.code || role.role_code}
                        </span>
                    ))}
                </div>
            );
        } else if (user.role) {
            // Fallback to old single role format
            return (
                <span className={`px-2 py-1 text-xs rounded-md capitalize ${getRoleBadgeClass(user.role)}`}>
                    {user.role}
                </span>
            );
        } else {
            // No roles
            return (
                <span className="px-2 py-1 text-xs rounded-md bg-gray-100 text-gray-800">
                    No Role
                </span>
            );
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Never';
        return formatDateTime(dateString);
    };

    return (
        <div className="overflow-x-auto border rounded-md">
            <table className="w-full">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="px-6 py-4 text-left whitespace-nowrap text-sm font-medium text-gray-900">
                            Username
                        </th>
                        <th className="px-6 py-4 text-left whitespace-nowrap text-sm font-medium text-gray-900">
                            Name
                        </th>
                        <th className="px-6 py-4 text-left whitespace-nowrap text-sm font-medium text-gray-900">
                            Mobile
                        </th>
                        <th className="px-6 py-4 text-left whitespace-nowrap text-sm font-medium text-gray-900">
                            Role
                        </th>
                        <th className="px-6 py-4 text-left whitespace-nowrap text-sm font-medium text-gray-900">
                            Status
                        </th>
                        <th className="px-6 py-4 text-left whitespace-nowrap text-sm font-medium text-gray-900">
                            Last Login
                        </th>
                        <th className="px-6 py-4 text-left whitespace-nowrap text-sm font-medium text-gray-900">
                            Actions
                        </th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {users.length === 0 ? (
                        <tr>
                            <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                                No users found
                            </td>
                        </tr>
                    ) : (
                        users.map((user) => (
                            <tr key={user.id} className="hover:bg-gray-50">
                                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600 capitalize">
                                    {user.username}
                                </td>
                                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600">
                                    {user.name}
                                </td>
                                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600">
                                    {user.mobile_number}
                                </td>
                                <td className="px-6 py-3 whitespace-nowrap text-sm">
                                    {renderRoles(user)}
                                </td>
                                <td className="px-6 py-3 whitespace-nowrap text-sm">
                                    <span className={`px-2 py-1 text-xs rounded-md w-full inline-block text-center capitalize ${user.is_active
                                            ? 'bg-green-100 text-green-800'
                                            : 'bg-red-100 text-red-800'
                                        }`}>
                                        {user.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                </td>
                                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600">
                                    {formatDate(user.last_login_at)}
                                </td>
                                <td className="px-6 py-3 whitespace-nowrap text-sm font-medium">
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => onEdit(user)}
                                            className="text-blue-600 hover:text-blue-800"
                                            title="Edit User"
                                        >
                                            <Edit2 className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => onUpdatePassword(user)}
                                            className="text-purple-600 hover:text-purple-800"
                                            title="Update Password"
                                        >
                                            <Key className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => onDelete(user.id)}
                                            className="text-red-600 hover:text-red-800"
                                            title="Delete User"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default UserTable;